import { useState } from "react";
import { Steps } from "@stories/atoms/Steps/Steps";
import { CompanyForm } from "@stories/molecules/CompanyForm/CompanyForm";
import { Elements } from "@stripe/react-stripe-js";
import { createDefaultOrganisation } from "@interfaces/organisation";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "@hooks/context/useAuthContext";
import { useProfileContext } from "@hooks/context/useProfileContext";
import {
  ProfileLineItem,
  ProfileStatus,
  ProfileType,
} from "@interfaces/profile";
import * as RouteHelper from "@utils/routes";
import { PageBreadcrumbs } from "@stories/atoms/PageBreadcrumbs/PageBreadcrumbs";
import { SubTitleWithImage } from "@stories/atoms/SubTitle/SubTitle";
import { useStep } from "@hooks/utils/useStep";
import { AdminProfileForm } from "@stories/molecules/AdminProfileForm/AdminProfileForm";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { Grid } from "@mui/material";
import { PageLoader } from "@stories/atoms/Loaders/PageLoader/PageLoader";
import { ToastOptions, toast } from "react-toastify";

const steps = ["Organisation Details", "Profile Details"];
const stripePromise = null; //loadStripe("pk_test_JnqbQaHNT8HAWBT4hEiTQ72N");

export const AdminAccount = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  if (!user) {
    throw Error("No user in state.");
  }
  const {
    isProfileLoading,
    userProfile,
    userOrganisation,
    createUserProfile,
    updateUserProfile,
  } = useProfileContext();

  const { activeStep, handleNext, handleBack } = useStep();

  const [loading, setLoading] = useState(false);
  const [organisation, setOrganisation] = useState(
    userOrganisation || createDefaultOrganisation()
  );

  if (isProfileLoading || !userProfile || !userOrganisation) {
    console.log("ACCOUNT PAGE LOADER");
    return <PageLoader />;
  }

  const finalizeAccount = async (profile: ProfileLineItem) => {
    try {
      setLoading(true);
      if (userProfile) {
        await updateUserProfile(
          {
            ...organisation,
            organisationLogo: organisation.organisationLogoSrc,
          },
          { ...profile, status: ProfileStatus.Active }
        );
      } else {
        await createUserProfile(
          {
            ...organisation,
            organisationLogo: organisation.organisationLogoSrc,
          },
          {
            ...profile,
            type: ProfileType.Admin,
            status: ProfileStatus.Active,
          }
        );
      }
      if (profile.status == ProfileStatus.Invited) {
        navigate(RouteHelper.listProjects);
      }
    } catch (e) {
      console.log(e);
      toast("Could not save account, please contact support.", {
        type: "error",
      } as ToastOptions);
    } finally {
      setLoading(false);
    }
  };

  const selectedCreateProfileStep = (() => {
    switch (activeStep) {
      default:
      case 0:
        return (
          <CompanyForm
            handleNext={handleNext}
            organisation={organisation}
            setOrganisation={setOrganisation}
          />
        );
      case 1:
        return (
          <AdminProfileForm
            loading={loading}
            handleBack={handleBack}
            profile={userProfile}
            finalizeAccount={finalizeAccount}
          />
        );
    }
  })();

  const title =
    userProfile.status !== ProfileStatus.Invited
      ? `${userOrganisation.tradingName} Account`
      : "Create Account";
  const breadcrumbs = [
    {
      path: RouteHelper.profile,
      name: "Profile",
    },
  ];

  return (
    <>
      {userProfile ? (
        <PageBreadcrumbs
          title={title}
          organisationLogo={userOrganisation?.organisationLogo || "User logo"}
          links={breadcrumbs}
        />
      ) : null}
      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid item xs={12} md={12}>
            <Steps activeStep={activeStep} steps={steps} />
          </Grid>
        </Grid>
      </StyledCard>

      <StyledCard>
        <Grid container spacing={2} sx={{ padding: "12px" }}>
          <Grid item xs={12} md={12}>
            <Elements stripe={stripePromise}>
              {selectedCreateProfileStep}
            </Elements>
          </Grid>
        </Grid>
      </StyledCard>
    </>
  );
};
